/* eslint-disable no-magic-numbers, max-lines*/
import { Bar } from "vue-chartjs";
import moment from "moment";
import store from "@/store";
import { createNamespacedHelpers } from "vuex";
import Toggle from "@/components/toggle/toggle.vue";
import { ChevronDownIcon, ChevronRightIcon } from "vue-feather-icons";
import { serviceTimeRangeToString } from "@/utils";

const { mapGetters: userGetters } = createNamespacedHelpers("user"),
	{ mapGetters: filtersGetters } = createNamespacedHelpers("filters"),
	{ mapGetters: brandingGetters } = createNamespacedHelpers("branding");

export default {
	extends: Bar,
	components: {
		Toggle,
		ChevronDownIcon,
		ChevronRightIcon,
	},

	props: {
		data: {
			type: Array,
			required: true,
		},
	},

	mounted() {
		this.renderChart(this.chartData, this.chartOptions);
	},

	watch: {
		isCostToggleActive() {
			this.renderChart(this.chartData, this.chartOptions);
		},
		chartData() {
			this.renderChart(this.chartData, this.chartOptions);
		},
	},

	data() {
		// sources: handling selections in Source view
		//	 [] -> show each source
		//	 [plate, preparation] -> show only selected sources
		// service_times: handling selections in Service time view
		//	 null -> if all are null, show cumulative data for each service time; otherwise nothing
		//	 [] -> show no data for that service time
		// 	 [breakfast, lunch] -> show ST data for that service time
		const selected = {
			sources: [],
			service_times: {},
		};
		for (const row of this.data)
			for (const setID of Object.keys(row.by_sts || {})) selected.service_times[setID] = null;

		// colors in order for every service time

		return {
			isCostToggleActive: false,
			selected,
			STColors: [],
		};
	},

	methods: {
		getRowLabel,
		getSTSColor,
		getSTColor,
		getSTSDataset,
		onSTSClick,
		onSTClick,
		onSourceClick,
		getSourceDatasets,
		getDatasets,
		getChartYAxisLabel,
		showST,
		showSource,
		applySaturationToHexColor,
		serviceTimeRangeToString,
	},

	computed: {
		sortedSTSetIDs,
		showCumulative,
		chartData,
		chartOptions,
		chartLabels,
		chartDatasetElementProperty,
		...userGetters(["activeOutlet", "weightUnitMinor", "weightFactorMinor", "serviceTimeSets"]),
		...filtersGetters(["activePeriod"]),
		...brandingGetters(["colors", "serviceTimesColors", "sourceWasteColors"]),
	},
};

function sortedSTSetIDs() {
	return Object.keys(this.selected.service_times)
		.map((k) => parseInt(k))
		.sort();
}

function onSourceClick(source) {
	if (this.selected.sources.length === 3)
		// if all are selected, keep only the clicked one

		this.selected.sources = this.selected.sources.filter((item) => item === source);
	else if (this.selected.sources.includes(source))
		this.selected.sources = this.selected.sources.filter((item) => item !== source);
	else this.selected.sources.push(source);
}

function onSTSClick(setID) {
	if (this.selected.service_times[setID] !== null) {
		this.selected.service_times[setID] = null;
		return;
	}

	this.selected.service_times[setID] = [];
}

function onSTClick(setID, STName) {
	if (this.serviceTimeSets[setID].gc_strategy === "TOTAL") return;

	// should never happen...
	const stsInSelected = this.selected.service_times[setID];
	if (stsInSelected === null) return;

	if (stsInSelected.includes(STName))
		this.selected.service_times[setID] = stsInSelected.filter((name) => name !== STName);
	else stsInSelected.push(STName);
}

function chartOptions() {
	const options = {
		title: { display: false },
		legend: { display: false },
		maintainAspectRatio: false,
		skipNull: true,
		scales: {
			xAxes: [
				{
					gridLines: {
						display: false,
						color: "rgba(0, 0, 0, 0)",
					},
					stacked: false,
					ticks: {
						fontSize: 11,
						fontColor: store.getters["branding/colors"][0],
						maxRotation: 45,
						fontFamily: "Nunito Sans",
					},
				},
			],
			yAxes: [
				{
					gridLines: { drawBorder: false },
					ticks: {
						beginAtZero: true,
						padding: 10,
						fontSize: 11,
						fontColor: store.getters["branding/colors"][0],
						fontFamily: "Nunito Sans",
						// Include a dollar sign in the ticks
						callback: (value) => this.getChartYAxisLabel(value),
					},
					stacked: false,
				},
			],
		},
	};

	return options;
}

// Round value to 2 decimals
function roundValue(value) {
	if (!value) return value;
	return parseFloat(value.toFixed(2));
}

function getSourceDatasets() {
	return ["production", "preparation", "plate"].map((source) => ({
		label: this.$t(`toggle_legend.${source}`),
		backgroundColor: this.sourceWasteColors[source],
		maxBarThickness: 40,
		hidden: !this.showSource(source),
		data: this.data.map((row) => {
			const value = row.by_source?.[source]?.wpg;
			return roundValue(value * this.weightFactorMinor) || null;
		}),
	}));
}

function showCumulative() {
	return Object.values(this.selected.service_times).every((status) => status === null);
}

function showST(setID, STName) {
	if (this.selected.service_times[setID] === null) return false;
	if (this.selected.service_times[setID].length === 0) return true;
	return this.selected.service_times[setID].includes(STName);
}

function showSource(source) {
	if (this.selected.sources.length === 0) return true;
	return this.selected.sources.includes(source);
}

function getSTSDataset(sts, STName) {
	if (!STName) {
		const shouldShowCumulative = sts.gc_strategy === "TOTAL" || this.showCumulative;
		return {
			label: sts.data.name,
			backgroundColor: this.getSTSColor(sts.id),
			hidden: !shouldShowCumulative,
			data: this.data.map((row) => {
				const value = row.by_sts?.[String(sts.id)]?.wpg;
				return roundValue(value * this.weightFactorMinor) || null;
			}),
		};
	}

	if (sts.gc_strategy === "SERVICE_TIME") {
		return {
			label: STName,
			backgroundColor: this.getSTColor(sts.id, STName),
			hidden: !this.showST(sts.id, STName),
			data: this.data.map((row) => {
				const value = row.by_sts?.[String(sts.id)]?.by_st?.[STName]?.wpg;
				return roundValue(value * this.weightFactorMinor) || null;
			}),
		};
	} else if (sts.gc_strategy === "TOTAL") {
		return {
			label: STName,
			backgroundColor: this.getSTColor(sts.id, STName),
			hidden: true,
			data: [],
		};
	}
}

function getDatasets() {
	if (this.chartDatasetElementProperty !== "service_time") return this.getSourceDatasets(this.selected.sources);

	// add ALL possible datasets; the chart component will take care of hiding
	// the ones that weren"t selected, through dataset.hidden
	const datasets = [];

	for (const setID in this.selected.service_times) {
		const sts = this.serviceTimeSets[setID];
		datasets.push(this.getSTSDataset(sts, null));
		for (const STName of sts.st_labels) datasets.push(this.getSTSDataset(sts, STName));
	}

	for (const dataset of datasets) dataset.maxBarThickness = 40;
	return datasets;
}

function chartData() {
	return {
		labels: this.chartLabels,
		datasets: this.getDatasets(),
	};
}

function getRowLabel(date) {
	// eslint-disable-next-line complexity
	switch (this.activePeriod) {
		case "day":
		case "week":
		case "month": {
			if (this.$utils.isClosed(date.toDate())) return this.$t("global.closed");

			const formattedDate = date.format("dd. DD.MM.YYYY");
			return this.$utils.replaceDoubleDotWithSingleDot(formattedDate);
		}
		case "year":
			return date.format("MMMM");
	}
}

function chartLabels() {
	return this.data.map((row) => moment(row.date)).map((date) => this.getRowLabel(date));
}

function chartDatasetElementProperty() {
	return this.isCostToggleActive ? "source" : "service_time";
}

function getChartYAxisLabel(value) {
	return `${value} ${this.weightUnitMinor}`;
}

function applySaturationToHexColor(hex, saturationPercent) {
	// Shamelessly borrowed and adapted from https://stackoverflow.com/a/31675514
	// Safe to say I don't know how any of this works ~ Dave
	/* eslint-disable no-console */
	if (!/^#([0-9a-f]{6})$/i.test(hex) || saturationPercent < 0 || saturationPercent > 100)
		console.error("Unexpected color format");
	/* eslint-enable no-console */

	const saturation = saturationPercent / 100,
		rgbIntensity = [
			parseInt(hex.substr(1, 2), 16) / 255,
			parseInt(hex.substr(3, 2), 16) / 255,
			parseInt(hex.substr(5, 2), 16) / 255,
		];

	const rgbIntensitySorted = rgbIntensity.slice(0).sort((a, b) => a - b);
	const maxIntensity = rgbIntensitySorted[2];
	const mediumIntensity = rgbIntensitySorted[1];
	const minIntensity = rgbIntensitySorted[0];

	if (maxIntensity === minIntensity) {
		// All colors have same intensity, which means
		// the original color is gray, so we can"t change saturation.
		return hex;
	}

	// New color max intensity wont change. Lets find medium and weak intensities.
	let newMediumIntensity;
	const newMinIntensity = maxIntensity * (1 - saturation);

	if (mediumIntensity === minIntensity) {
		// Weak colors have equal intensity.
		newMediumIntensity = newMinIntensity;
	} else {
		// Calculate medium intensity with respect to original intensity proportion.
		const maxDelta = maxIntensity - mediumIntensity;
		const minDelta = mediumIntensity - minIntensity;
		const proportion = maxDelta / minDelta;
		newMediumIntensity = (proportion * newMinIntensity + maxIntensity) / (proportion + 1);
	}

	const newRgbIntensity = [];
	const newRgbIntensitySorted = [newMinIntensity, newMediumIntensity, maxIntensity];

	// We"ve found new intensities, but we have then sorted from min to max.
	// Now we have to restore original order.
	rgbIntensity.forEach(function (originalRgb) {
		const rgbSortedIndex = rgbIntensitySorted.indexOf(originalRgb);
		newRgbIntensity.push(newRgbIntensitySorted[rgbSortedIndex]);
	});

	const floatToHex = (val) => ("0" + Math.round(val * 255).toString(16)).substr(-2);
	const rgb2hex = (rgb) => "#" + floatToHex(rgb[0]) + floatToHex(rgb[1]) + floatToHex(rgb[2]);

	return rgb2hex(newRgbIntensity);
}

function getSTSColor(setID) {
	const index = this.sortedSTSetIDs.indexOf(parseInt(setID));

	/* eslint-disable no-console */
	if (index >= this.serviceTimesColors.length) console.error("There are not enough service time colors defined...");
	/* eslint-enable no-console */

	return this.serviceTimesColors[index % this.serviceTimesColors.length];
}

/**- For strategy `SERVICE_TIME` we show different saturation colors
 * - For strategy `TOTAL` we use the same color saturation always */
function getSTColor(setID, STName) {
	const baseColor = this.getSTSColor(setID);
	if (this.serviceTimeSets[setID].gc_strategy === "TOTAL") return baseColor;

	const index = this.serviceTimeSets[setID].st_labels.indexOf(STName);
	return this.applySaturationToHexColor(baseColor, 10 + index * 15);
}
